import React, { useState, useEffect, useRef } from "react";
import {
  FolderKanban,
  Wallet,
  CalendarCheck,
  ClipboardCheck,
  Landmark,
  Building2,
  Download,
  Eye,
  FileText,
  TrendingUp,
  PieChart as PieChartIcon,
  AlertCircle,
  Users,
  Target,
  DollarSign
} from "lucide-react";
import Button from "components/ui/Button";
import MetricCard from "components/dashboard/MetricCard";
import DashboardChartFrame from "components/dashboard/DashboardChartFrame";
import AlertPanel from "components/dashboard/AlertPanel";
import { useLocationContext } from "context/LocationContext";
import { formatIgfCurrency, getDistrictDashboardData, YEARS } from "utils/dashboardData";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Line
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Color palettes
const SECTOR_COLORS = {
  Education: "hsl(215, 60%, 22%)",
  Infrastructure: "hsl(174, 60%, 40%)",
  Health: "hsl(38, 92%, 50%)",
  "Water and Sanitation": "hsl(280, 50%, 50%)",
  Agriculture: "hsl(152, 60%, 40%)",
  Governance: "hsl(12, 76%, 58%)",
  Commerce: "hsl(215, 15%, 50%)",
};

const DIMENSION_COLORS = [
  'hsl(215, 60%, 22%)',
  'hsl(142, 70%, 45%)',
  'hsl(38, 92%, 50%)',
  'hsl(262, 52%, 55%)',
  'hsl(340, 82%, 52%)',
  'hsl(174, 60%, 40%)',
  'hsl(0, 72%, 51%)',
];

const MEETING_TYPE_COLORS = {
  "DPAT Meeting": "hsl(215, 60%, 22%)",
  "Technical Review": "hsl(142, 70%, 45%)",
  "Stakeholder Forum": "hsl(38, 92%, 50%)",
  "Planning Session": "hsl(262, 52%, 55%)",
  "Review Meeting": "hsl(340, 82%, 52%)",
  "Training Workshop": "hsl(174, 60%, 40%)",
};

export default function DistrictDashboard() {
  const currentYear = new Date().getFullYear();
  const defaultYear = String(currentYear - 1);

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const dashboardRef = useRef(null);
  const { orgTree, currentLocation } = useLocationContext();

  useEffect(() => {
    const loadData = async () => {
      if (!currentLocation) return;
      setLoading(true);
      try {
        const data = await getDistrictDashboardData(orgTree, currentLocation, selectedYear);
        setDashboardData(data);
      } catch (error) {
        console.error("Error loading district dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgTree, currentLocation, selectedYear]);

  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    setIsExporting(true);
    try {
      const element = dashboardRef.current;
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      element.style.overflow = originalOverflow;
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.height;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.height;
      }
      pdf.save(`district-dashboard-${dashboardData?.districtName}-${selectedYear}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const previewPDF = async () => {
    if (!dashboardRef.current) return;
    setIsExporting(true);
    setShowPreview(true);
    try {
      const element = dashboardRef.current;
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      element.style.overflow = originalOverflow;
      setPreviewUrl(canvas.toDataURL('image/png'));
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview.');
      setShowPreview(false);
    } finally {
      setIsExporting(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
  };

  const downloadFromPreview = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.download = `district-dashboard-${dashboardData?.districtName}-${selectedYear}.png`;
      link.href = previewUrl;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="main-content flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-2">Loading dashboard for {selectedYear}...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="main-content flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="bg-muted/30 rounded-full p-3 mb-3">
            <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-sm text-muted-foreground">No data available for this district</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Please select a different district or year</p>
        </div>
      </div>
    );
  }

  // Calculate IGF collection percentage
  const igfPercentage = dashboardData.summaryStats?.totalIgfBudgeted > 0
    ? (dashboardData.summaryStats.totalIgfCollected / dashboardData.summaryStats.totalIgfBudgeted) * 100
    : 0;

  return (
    <>
      <div className="main-content space-y-6 animate-fade-in">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-xl font-bold sm:text-2xl">
              <Building2 className="h-6 w-6 text-accent" />
              District Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {dashboardData.districtName} - Projects, budget and governance for {dashboardData.year}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground">
                Yearly View - {dashboardData.year}
              </span>
              <span className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 text-[11px] text-muted-foreground">
                {dashboardData.alerts?.length || 0} active alerts
              </span>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="
                w-full
                sm:w-auto
                appearance-none
                rounded-xl
                border border-slate-300
                bg-white
                px-4
                py-2.5
                pr-10
                text-sm
                font-medium
                text-slate-700
                shadow-sm
                transition-all
                duration-200
                hover:border-slate-400
                focus:border-blue-500
                focus:outline-none
                focus:ring-4
                focus:ring-blue-100
                dark:border-slate-700
                dark:bg-slate-900
                dark:text-slate-200
                dark:hover:border-slate-600
                dark:focus:border-blue-400
                dark:focus:ring-blue-900/30
              "
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Button className="w-full sm:w-auto" variant="outline" size="sm" type="button" onClick={previewPDF} disabled={isExporting}>
              <Eye className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button className="w-full sm:w-auto" variant="outline" size="sm" type="button" onClick={exportToPDF} disabled={isExporting}>
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-1"></div>
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        <div ref={dashboardRef}>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard
              title="Activity Plans"
              value={dashboardData.metrics.activityPlan.value}
              subMetrics={dashboardData.metrics.activityPlan.subMetrics}
              icon={ClipboardCheck}
              status="info"
            />
            <MetricCard
              title="Projects"
              value={dashboardData.metrics.activeProjects.value}
              icon={FolderKanban}
              status="success"
            />
            <MetricCard
              title="Programmes"
              value={dashboardData.metrics.programmes?.value || "0"}
              icon={CalendarCheck}
              status="warning"
            />
            <MetricCard
              title="IGF Budgeted"
              value={dashboardData.metrics.igfBudgeted?.value || "GHS 0"}
              icon={Target}
              status="info"
            />
            <MetricCard
              title="IGF Collected"
              value={dashboardData.metrics.igfCollected.value}
              subMetrics={dashboardData.metrics.igfCollected.subMetrics}
              icon={Landmark}
              status="success"
            />
            <MetricCard
              title="Meetings"
              value={dashboardData.metrics.meetingsHeld.value}
              icon={Users}
              status="warning"
            />
          </div>

          {/* Execution Rate Card */}
          {dashboardData.summaryStats && (
            <div className="metric-card mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Activity Plan Execution Rate</p>
                  <p className="text-2xl font-bold">{dashboardData.summaryStats.activityExecutionRate || 0}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dashboardData.summaryStats.plansExecuted || 0} of {dashboardData.summaryStats.totalAAP || 0} plans executed
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-accent opacity-50" />
              </div>
              <div className="mt-2 w-full bg-muted rounded-full h-2">
                <div
                  className="bg-accent h-2 rounded-full transition-all"
                  style={{ width: `${dashboardData.summaryStats.activityExecutionRate || 0}%` }}
                />
              </div>
            </div>
          )}

          {/* IGF Collection Rate Card */}
          {dashboardData.summaryStats && dashboardData.summaryStats.totalIgfBudgeted > 0 && (
            <div className="metric-card mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">IGF Collection Rate</p>
                  <p className="text-2xl font-bold">{Math.round(igfPercentage)}%</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatIgfCurrency(dashboardData.summaryStats.totalIgfCollected)} of {formatIgfCurrency(dashboardData.summaryStats.totalIgfBudgeted)} budgeted
                  </p>
                </div>
                <Landmark className="h-8 w-8 text-green-500 opacity-50" />
              </div>
              <div className="mt-2 w-full bg-muted rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(igfPercentage, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* IGF Budget vs Collected by Source */}
          <div className="metric-card mt-6">
            <h3 className="font-semibold text-sm mb-4">IGF Budget vs Collected by Source</h3>
            {dashboardData.igfBySource && dashboardData.igfBySource.length > 0 ? (
              <DashboardChartFrame minWidthClass="min-w-[720px] sm:min-w-0">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={dashboardData.igfBySource} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => `GHS ${(value / 1000000).toFixed(1)}M`} />
                    <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [`GHS ${Number(value).toLocaleString()}`, 'Amount']}
                      labelFormatter={(label) => `Source: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="budgeted" name="Budgeted" fill="hsl(215, 60%, 22%)" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="collected" name="Collected" fill="hsl(142, 70%, 45%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DashboardChartFrame>
            ) : (
              <div className="flex items-center justify-center h-[400px]">
                <p className="text-sm text-muted-foreground">No IGF data available</p>
              </div>
            )}
          </div>

          {/* Projects by Sector & Development Dimension */}
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Projects by Sector</h3>
              {dashboardData.sectorBreakdown && dashboardData.sectorBreakdown.length > 0 ? (
                <DashboardChartFrame minWidthClass="min-w-[520px] sm:min-w-0">
                  <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardData.sectorBreakdown}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={true}
                    >
                      {dashboardData.sectorBreakdown.map((entry, i) => (
                        <Cell key={i} fill={SECTOR_COLORS[entry.name] || DIMENSION_COLORS[i % DIMENSION_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [`${value} projects`, props.payload.name]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-sm text-muted-foreground">No sector data available</p>
                </div>
              )}
            </div>

            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Projects by Development Dimension</h3>
              {dashboardData.projectsByDimension && Object.keys(dashboardData.projectsByDimension).length > 0 ? (
                <DashboardChartFrame>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={Object.entries(dashboardData.projectsByDimension).map(([name, value]) => ({ name, value }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => [`${value} projects`, 'Count']} />
                      <Bar dataKey="value" fill="hsl(262, 52%, 55%)" radius={[4, 4, 0, 0]}>
                        {Object.entries(dashboardData.projectsByDimension).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={DIMENSION_COLORS[index % DIMENSION_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-sm text-muted-foreground">No dimension data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Meetings by Type & Participation */}
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Meetings by Type</h3>
              {dashboardData.meetingsByType && dashboardData.meetingsByType.length > 0 ? (
                <DashboardChartFrame minWidthClass="min-w-[520px] sm:min-w-0">
                  <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardData.meetingsByType}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {dashboardData.meetingsByType.map((entry, i) => (
                        <Cell key={i} fill={MEETING_TYPE_COLORS[entry.name] || DIMENSION_COLORS[i % DIMENSION_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-sm text-muted-foreground">No meeting data available</p>
                </div>
              )}
            </div>

            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Meeting Participation</h3>
              {dashboardData.meetingParticipation ? (
                <DashboardChartFrame>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardData.meetingParticipation}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="male" name="Male" fill="hsl(215, 60%, 22%)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="female" name="Female" fill="hsl(340, 82%, 52%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </DashboardChartFrame>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-sm text-muted-foreground">No participation data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Project Tracker Table */}
          <div className="metric-card mt-6">
            <h3 className="font-semibold text-sm mb-4">Project Tracker</h3>
            <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-muted-foreground">#</th>
                    <th className="text-left py-2 font-medium text-muted-foreground">Project Name</th>
                    <th className="text-left py-2 font-medium text-muted-foreground">Development Dimension</th>
                    <th className="text-left py-2 font-medium text-muted-foreground">Sector</th>
                    <th className="text-right py-2 font-medium text-muted-foreground">Contract Sum (GHS)</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.projectRows && dashboardData.projectRows.length > 0 ? (
                    dashboardData.projectRows.map((project, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <td className="py-2.5">{idx + 1}</td>
                        <td className="py-2.5 font-medium">{project.name || `Project ${idx + 1}`}</td>
                        <td className="py-2.5 text-muted-foreground">{project.dd || 'Not Specified'}</td>
                        <td className="py-2.5 text-muted-foreground">{project.sector || 'Not Specified'}</td>
                        <td className="py-2.5 text-right font-medium">
                          GHS {(project.contractSum / 1000000).toFixed(2)}M
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground">
                        No projects found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alerts Panel */}
          <div className="mt-6">
            <AlertPanel alerts={dashboardData.alerts || []} />
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm animate-fade-in sm:items-center sm:p-4">
          <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-slate-900 sm:max-h-[90vh] sm:rounded-2xl">
            <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                <h3 className="truncate text-sm font-semibold sm:text-base">Dashboard Preview - {dashboardData?.districtName} ({selectedYear})</h3>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button className="w-full sm:w-auto" variant="outline" size="sm" onClick={downloadFromPreview}>
                  <Download className="h-4 w-4 mr-1" />
                  Download as PNG
                </Button>
                <Button className="w-full sm:w-auto" variant="outline" size="sm" onClick={closePreview}>
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <img
                src={previewUrl}
                alt="Dashboard Preview"
                className="w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: 'calc(90vh - 80px)', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
